export enum productWidgetColors {
  blue = "#2e3a8c#ffffff",
  green = "#3B755F#ffffff",
  beige = "#F2EBDB#3B755F",
  white = "#ffffff#3B755F",
  black = "#000000#ffffff",
}

export type WidgetType = "carbon" | "plastic bottles" | "trees";

export const WidgetTypeDict = {
  carbon: "kg of carbon",
  "plastic bottles": " plastic bottles",
  trees: " trees",
};

export type productWidgetActions = "collects" | "plants" | "offsets";

export interface IProductWidget {
  readonly id: number;
  readonly type: WidgetType;
  readonly action: productWidgetActions;
  readonly amount: number;
  active: boolean;
  linked: boolean;
  selectedColor: productWidgetColors;
}
export interface IProductWidgetProps extends IProductWidget {
  availableColors: string[];
  handleCheckboxClick: (e: any) => void;
  handleSwitchClick: (e: any) => void;
  handleColorClick: (e: any, color: string) => void;
  handleOnMouseEnter: (e: any) => void;
  handleInfoMarkFocus: (e: any) => void;
  handleInfoMarkBlur: (e: any) => void;
}

export enum Colors {
  // Primary Green
  primaryGreen1 = "#003d15",
  primaryGreen2 = "#00521d",
  primaryGreen3 = "#007028",
  primaryGreen4 = "#008f32",
  primaryGreen5 = "#1db954",
  primaryGreen6 = "#20cb5c",
  primaryGreen7 = "#2cdd80",
  primaryGreen8 = "#58e49b",
  primaryGreen9 = "#84ebb5",
  primaryGreen10 = "#caf6df",
  primaryGreen11 = "#eefcf4",

  // Primary Gray
  primaryGray1 = "#0f0f0f",
  primaryGray2 = "#1f1f1f",
  primaryGray3 = "#2e2e2e",
  primaryGray4 = "#3d3d3d",
  primaryGray5 = "#4d4d4d",
  primaryGray6 = "#5c5c5c",
  primaryGray7 = "#6b6b6b",
  primaryGray8 = "#7a7a7a",
  primaryGray9 = "#8a8a8a",
  primaryGray10 = "#999999",
  primaryGray11 = "#a8a8a8",
  primaryGray12 = "#b8b8b8",
  primaryGray13 = "#c7c7c7",
  primaryGray14 = "#d6d6d6",
  primaryGray15 = "#e6e6e6",
  primaryGray16 = "#f5f5f5",
  white = "#ffffff",
  black = "#000000",

  // Secondary Purple
  secondaryPurple1 = "#1a004d",
  secondaryPurple2 = "#220066",
  secondaryPurple3 = "#330099",
  secondaryPurple4 = "#4b00cc",
  secondaryPurple5 = "#661aff",
  secondaryPurple6 = "#7e33ff",
  secondaryPurple7 = "#9e66ff",
  secondaryPurple8 = "#c599ff",
  secondaryPurple9 = "#e7ccff",
  secondaryPurple10 = "#f6e5ff",

  // Secondary Yellow
  secondaryYellow1 = "#474100",
  secondaryYellow2 = "#665e00",
  secondaryYellow3 = "#857a00",
  secondaryYellow4 = "#a39600",
  secondaryYellow5 = "#c2b200",
  secondaryYellow6 = "#e0ce00",
  secondaryYellow7 = "#fae500",
  secondaryYellow8 = "#ffef3d",
  secondaryYellow9 = "#fff370",
  secondaryYellow10 = "#fff8ad",
  secondaryYellow11 = "#fffcd6",

  // Secondary Orange
  secondaryOrange1 = "#8f3200",
  secondaryOrange2 = "#c24400",
  secondaryOrange3 = "#e65100",
  secondaryOrange4 = "#ef6c00",
  secondaryOrange5 = "#f57c00",
  secondaryOrange6 = "#fb8c00",
  secondaryOrange7 = "#ff9800",
  secondaryOrange8 = "#ffa726",
  secondaryOrange9 = "#ffb74d",
  secondaryOrange10 = "#ffcc80",
  secondaryOrange11 = "#ffe0b2",
  secondaryOrange12 = "#fff3e0",

  // Tertiary Red
  tertiaryRed1 = "#7b0300",
  tertiaryRed2 = "#a3000e",
  tertiaryRed3 = "#cc0300",
  tertiaryRed4 = "#f50400",
  tertiaryRed5 = "#ff332e",
  tertiaryRed6 = "#ff5452",
  tertiaryRed7 = "#ff7370",
  tertiaryRed8 = "#ff9999",
  tertiaryRed9 = "#ffbdbd",
  tertiaryRed10 = "#ffd6d6",
  tertiaryRed11 = "#ffe8e8",

  // Tertiary Magenta
  tertiaryMagenta1 = "#5c0048",
  tertiaryMagenta2 = "#7a005f",
  tertiaryMagenta3 = "#990077",
  tertiaryMagenta4 = "#c20097",
  tertiaryMagenta5 = "#e000af",
  tertiaryMagenta6 = "#ff1fce",
  tertiaryMagenta7 = "#ff47d7",
  tertiaryMagenta8 = "#ff70e2",
  tertiaryMagenta9 = "#ff99eb",
  tertiaryMagenta10 = "#ffb8f1",
  tertiaryMagenta11 = "#ffd6f7",
  tertiaryMagenta12 = "#ffebfb",

  // Tertiary Cyan
  tertiaryCyan1 = "#002929",
  tertiaryCyan2 = "#003d3d",
  tertiaryCyan3 = "#005c5c",
  tertiaryCyan4 = "#007a7a",
  tertiaryCyan5 = "#008f8f",
  tertiaryCyan6 = "#00b8b8",
  tertiaryCyan7 = "#00e0e0",
  tertiaryCyan8 = "#33ffff",
  tertiaryCyan9 = "#70ffff",
  tertiaryCyan10 = "#adffff",
  tertiaryCyan11 = "#d6ffff",
  tertiaryCyan12 = "#ebffff",

  // Tertiary Lime
  tertiaryLime1 = "#2a3d00",
  tertiaryLime2 = "#375200",
  tertiaryLime3 = "#537a00",
  tertiaryLime4 = "#618f00",
  tertiaryLime5 = "#7db800",
  tertiaryLime6 = "#9feb00",
  tertiaryLime7 = "#acff00",
  tertiaryLime8 = "#c1ff3d",
  tertiaryLime9 = "#d1ff70",
  tertiaryLime10 = "#e5ffad",
  tertiaryLime11 = "#f3ffd6",

  // Tertiary Blue
  tertiaryBlue1 = "#002152",
  tertiaryBlue2 = "#00317a",
  tertiaryBlue3 = "#0041a3",
  tertiaryBlue4 = "#0056d6",
  tertiaryBlue5 = "#0066ff",
  tertiaryBlue6 = "#297fff",
  tertiaryBlue7 = "#5297ff",
  tertiaryBlue8 = "#7aafff",
  tertiaryBlue9 = "#99c2ff",
  tertiaryBlue10 = "#b8d4ff",
  tertiaryBlue11 = "#d6e6ff",
  tertiaryBlue12 = "#eaf2ff",
  tertiaryBlue13 = "#f3f8ff",

  // Grayscale Black
  grayscaleBlack1 = "#606060",
  grayscaleBlack2 = "#404040",
  grayscaleBlack3 = "#303030",
  grayscaleBlack4 = "#202020",
  grayscaleBlack5 = "#101010",
}
